import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import { rupeeGross, type PriceComponent } from "./priceList";

/**
 * Plans › Create plan (plan builder design §9.12 C5, owner 2026-10-09).
 *
 *   POST /plan/{ns}   radaccess plan:write -> radcatalog POST /planspec/{ns}
 *
 * One call builds the whole plan: its RADIUS group, check/reply rows and its
 * one price record. Picked from the price list, the plan is LINKED to that
 * compiled price record (component_id) and never gets a second one.
 *
 * The rules below MIRROR radcatalog app/planspec/spec.go so the operator sees
 * before Save what the server will build; the server still decides.
 */

/** The body of POST /planspec, money in rupees. */
export interface PlanSpecRequest {
  plan_uid: string;
  speed_mbps: number;
  valid_days: number;
  devices: number;
  daily_minutes?: number;
  price: number;
  price_includes_gst: true;
  gst_percent: number;
  heading?: string;
  component_id?: string;
}

/** radcatalog's 201/200 answer. */
export interface PlanSpecResult {
  namespace: string;
  groupname: string;
  groupuid: string;
  component_id: string;
  linked?: boolean;
  heading: string;
  description: string;
  gross: number;
  net: number;
  gst_percent: number;
  created: { group: boolean; rows: number; component: boolean };
}

/** radcatalog's refusal body once anything was written (planspec.Failure). */
export interface PlanSpecFailure {
  error: string;
  failed?: string;
  written?: { group: boolean; rows: number; component: boolean };
}

const enc = encodeURIComponent;

export async function buildPlan(identity: CallerIdentity, ns: string, req: PlanSpecRequest): Promise<PlanSpecResult> {
  const r = await callGateway<PlanSpecResult>(identity, config().gatewayBase, `/plan/${enc(ns)}`, { method: "POST", body: req });
  return r.data;
}

/**
 * Who may press Save (owner C5-2, 2026-10-09). radcatalog forwards the
 * caller's token to radpolicy (create: superadmin, ossadmin) and radbilling
 * (create: superadmin, bssadmin): only superadmin satisfies both today. The
 * gateway admits Operator and bssadmin too, so the form is shown to them with
 * the reason. ⚠️ DISPLAY ONLY; widen it when the per-operator service account
 * exists, not before.
 */
export function canSavePlan(roles: string[]): boolean {
  return roles.some((r) => r.toLowerCase() === "superadmin");
}

/** Who sees Plans › Create plan: the gateway's plan:write holders. */
export function canOpenCreatePlan(roles: string[]): boolean {
  return roles.some((r) => ["superadmin", "bssadmin", "operator", "manager"].includes(r.toLowerCase()));
}

export interface PlanForm {
  speedMbps: string;
  validDays: string;
  devices: string;
  dailyMinutes: string;
  /** Whole rupees, including GST: what the subscriber pays. */
  price: string;
  gstPercent: number;
  heading: string;
  /** Set when the plan is picked from the price list. */
  componentId?: string;
}

export const EMPTY_FORM: PlanForm = { speedMbps: "", validDays: "30", devices: "1", dailyMinutes: "", price: "", gstPercent: 18, heading: "" };

export type FormProblems = Partial<Record<"speedMbps" | "validDays" | "devices" | "dailyMinutes" | "price" | "heading", string>>;

const whole = (s: string): number | null => (/^\d+$/.test(s.trim()) ? Number(s.trim()) : null);

/** The same bounds radcatalog Validate refuses with a 400. */
export function validate(f: PlanForm): FormProblems {
  const p: FormProblems = {};
  const speed = whole(f.speedMbps);
  if (speed === null || speed < 1 || speed > 1000) p.speedMbps = "1 to 1000 Mbps";
  const days = whole(f.validDays);
  if (days === null || days < 1 || days > 730) p.validDays = "1 to 730 days, counted from the first login";
  const devices = whole(f.devices);
  if (devices === null || devices < 1 || devices > 10) p.devices = "1 to 10 devices";
  if (f.dailyMinutes.trim() !== "") {
    const m = whole(f.dailyMinutes);
    if (m === null || m < 1 || m > 1440) p.dailyMinutes = "1 to 1440 minutes, or leave empty for no daily limit";
  }
  const price = whole(f.price);
  if (price === null || price < 1) p.price = "whole rupees including GST, at least ₹1";
  if (f.heading.trim().length > 255) p.heading = "at most 255 characters";
  return p;
}

/** Net stored on the price record: gross ÷ (1 + GST), to the paisa (radcatalog Money). */
export function netOf(gross: number, gstPercent: number): number {
  return Math.round((gross / (1 + gstPercent / 100)) * 100) / 100;
}

/**
 * The plan code radcatalog will write: 10M-30D-2U[-120MIN]-A7F3 (owner
 * 2026-09-19), the last part the first 4 hex characters of the plan's UUID.
 * The server widens it to 6 or 8 characters if 4 is taken, so this is a
 * preview, labelled as such.
 */
export function planCode(speed: number, days: number, devices: number, dailyMinutes: number | null, uid: string): string {
  let p = `${speed}M-${days}D-${devices}U`;
  if (dailyMinutes) p += `-${dailyMinutes}MIN`;
  return `${p}-${uid.replace(/-/g, "").slice(0, 4).toUpperCase()}`;
}

export function toRequest(f: PlanForm, planUid: string): PlanSpecRequest {
  const req: PlanSpecRequest = {
    plan_uid: planUid,
    speed_mbps: Number(f.speedMbps),
    valid_days: Number(f.validDays),
    devices: Number(f.devices),
    price: Number(f.price),
    price_includes_gst: true,
    gst_percent: f.gstPercent,
  };
  if (f.dailyMinutes.trim() !== "") req.daily_minutes = Number(f.dailyMinutes);
  if (f.heading.trim() !== "") req.heading = f.heading.trim();
  if (f.componentId) req.component_id = f.componentId;
  return req;
}

/** A compiled price-list row a plan can be built from. */
export interface PickableRow {
  component: PriceComponent;
  speedMbps: number;
  validDays: number;
  /** Whole rupees including GST, as published. */
  gross: number;
}

/**
 * The price-list rows a new plan can adopt: active, recurring internet
 * prices not yet linked to a plan, labelled with a speed and a term, at a
 * whole-rupee price -- exactly what radcatalog CheckPicked accepts. Anything
 * else is left out rather than offered and refused.
 */
export function pickableRows(components: PriceComponent[]): PickableRow[] {
  const out: PickableRow[] = [];
  for (const c of components) {
    if (!c.active || !c.pureinternetservice || c.billingtype !== "RECURRING" || c.radcatalog_groupuid) continue;
    const speed = whole(c.labels?.speed_mbps ?? "");
    const days = whole(c.labels?.term_days ?? "");
    if (speed === null || days === null || speed < 1 || speed > 1000 || days < 1 || days > 730) continue;
    out.push({ component: c, speedMbps: speed, validDays: days, gross: rupeeGross(c.price, c.gstpercent) });
  }
  return out.sort((a, b) => a.speedMbps - b.speedMbps || a.validDays - b.validDays || a.component.name.localeCompare(b.component.name));
}

/** Fill the form from a picked row; speed, days, price and GST come from it. */
export function pick(f: PlanForm, row: PickableRow): PlanForm {
  return {
    ...f,
    speedMbps: String(row.speedMbps),
    validDays: String(row.validDays),
    price: String(row.gross),
    gstPercent: row.component.gstpercent,
    heading: f.heading || row.component.name,
    componentId: row.component.id,
  };
}

/** Back to typing it in: the price-list row is no longer the price. */
export function unpick(f: PlanForm): PlanForm {
  const { componentId: _dropped, ...rest } = f;
  void _dropped;
  return rest;
}

/**
 * Create all durations (owner 2026-10-26: "one-click create all duration").
 * A basic plan already on sale -- a linked price record with product and
 * speed labels -- and the price-list rows of the same product and speed
 * that no plan sells yet. Each one is built as its own plan, copying the
 * basic plan's devices and daily minutes; only the days and the price differ,
 * and the price is always the row's own (radcatalog CheckPicked re-checks it).
 */
export interface DurationGroup {
  key: string;
  label: string;
  /** The plan the limits are copied from: the shortest term on sale; absent when none of this speed is (one device). */
  base?: PriceComponent;
  devices: number;
  dailyMinutes: number | null;
  rows: PickableRow[];
  /** Why the group cannot be built in one click; "" when it can. */
  problem: string;
}

const PLAN_CODE_RE = /^\d+M-\d+D-(\d+)U(?:-(\d+)MIN)?-[0-9A-F]+$/i;

/** Devices and daily minutes from a plan code 30M-30D-1U[-120MIN]-74F8; null when the name is not a plan code. */
export function codeLimits(groupname: string): { devices: number; dailyMinutes: number | null } | null {
  const m = PLAN_CODE_RE.exec(groupname.trim());
  if (!m) return null;
  return { devices: Number(m[1]), dailyMinutes: m[2] ? Number(m[2]) : null };
}

const PRODUCT_NAMES: Record<string, string> = { residential: "Residential", commercial: "Commercial", hotspot: "Hotspot" };

/**
 * Every basic plan the price list prices and no plan sells yet, by product and
 * speed (owner 2026-10-26: every basic plan goes on sale; add-ons are sold
 * with them). A speed already on sale copies its plan's devices and minutes;
 * a speed with none on sale gets one device -- an Extra login add-on raises it.
 */
export function missingDurations(components: PriceComponent[]): DurationGroup[] {
  const waiting = pickableRows(components);
  const linked = components.filter(
    (c) => c.active && c.pureinternetservice && c.billingtype === "RECURRING" && c.radcatalog_groupname && whole(c.labels?.speed_mbps ?? "") !== null,
  );
  const groups = new Map<string, PriceComponent[]>();
  for (const c of linked) {
    const k = `${c.labels?.product ?? ""}/${c.labels?.speed_mbps}`;
    groups.set(k, [...(groups.get(k) ?? []), c]);
  }
  const out: DurationGroup[] = [];
  const unsold = new Map<string, PickableRow[]>();
  for (const w of waiting) {
    const k = `${w.component.labels?.product ?? ""}/${w.speedMbps}`;
    if (!w.component.labels?.product || groups.has(k)) continue;
    unsold.set(k, [...(unsold.get(k) ?? []), w]);
  }
  for (const [key, rows] of unsold) {
    const product = rows[0]?.component.labels?.product ?? "";
    out.push({ key, label: `${PRODUCT_NAMES[product] ?? product} ${rows[0]?.speedMbps} Mbps`, devices: Number(rows[0]?.component.labels?.devices) || 1, dailyMinutes: null, rows, problem: "" });
  }
  for (const [key, plans] of groups) {
    const first = plans[0];
    if (!first) continue;
    const [product, speed] = [first.labels?.product ?? "", first.labels?.speed_mbps ?? ""];
    const rows = waiting.filter((r) => (r.component.labels?.product ?? "") === product && String(r.speedMbps) === speed);
    if (!rows.length) continue;
    const term = (c: PriceComponent) => whole(c.labels?.term_days ?? "") ?? Number.MAX_SAFE_INTEGER;
    const sorted = [...plans].sort((a, b) => term(a) - term(b) || (a.radcatalog_groupname ?? "").localeCompare(b.radcatalog_groupname ?? ""));
    const base = sorted[0] ?? first;
    const limits = codeLimits(base.radcatalog_groupname ?? "");
    const shortest = sorted.filter((c) => term(c) === term(base)).map((c) => codeLimits(c.radcatalog_groupname ?? ""));
    const differ = shortest.some((l) => !l || !limits || l.devices !== limits.devices || l.dailyMinutes !== limits.dailyMinutes);
    out.push({
      key,
      label: `${PRODUCT_NAMES[product] ?? product} ${speed} Mbps`.trim(),
      base,
      devices: limits?.devices ?? 0,
      dailyMinutes: limits?.dailyMinutes ?? null,
      rows,
      problem: !limits
        ? `plan ${base.radcatalog_groupname} has no plan code to copy devices from -- build these one at a time below`
        : differ
          ? `${shortest.length} plans of this speed differ in devices or minutes -- build these one at a time below`
          : "",
    });
  }
  const speedOf = (g: DurationGroup) => g.rows[0]?.speedMbps ?? 0;
  return out.sort((a, b) => a.key.split("/")[0]!.localeCompare(b.key.split("/")[0]!) || speedOf(a) - speedOf(b));
}

/**
 * The plan_uid for a row's duration plan: a name-based UUID (version 5 layout)
 * of the namespace and the price record, so pressing the button again after
 * a timeout finishes the same plan instead of building a second one.
 */
export async function durationPlanUid(ns: string, componentId: string): Promise<string> {
  const data = new TextEncoder().encode(`ankhos/plan-duration/${ns}/${componentId.toLowerCase()}`);
  const b = new Uint8Array(await crypto.subtle.digest("SHA-1", data)).slice(0, 16);
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x50;
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** The planspec body for one duration of a group, priced by its row. */
export function durationRequest(g: DurationGroup, row: PickableRow, planUid: string): PlanSpecRequest {
  const req: PlanSpecRequest = {
    plan_uid: planUid,
    speed_mbps: row.speedMbps,
    valid_days: row.validDays,
    devices: g.devices,
    price: row.gross,
    price_includes_gst: true,
    gst_percent: row.component.gstpercent,
    heading: row.component.name,
    component_id: row.component.id,
  };
  if (g.dailyMinutes) req.daily_minutes = g.dailyMinutes;
  return req;
}
